import React from 'react';
import styled from 'styled-components';

export interface PipelineStage {
  label: string;
  count: number;
  color: string;
}

interface PipelineChartProps {
  stages: PipelineStage[];
  /** Optional title above the chart */
  title?: string;
  /** Optional meta text (e.g. total count) */
  meta?: string;
  className?: string;
}

export const PipelineChart: React.FC<PipelineChartProps> = ({
  stages,
  title,
  meta,
  className
}) => {
  const total = stages.reduce((sum, s) => sum + s.count, 0);

  return (
    <Wrapper className={className}>
      {(title || meta) && (
        <Head>
          {title && <Title>{title}</Title>}
          {meta && <Meta>{meta}</Meta>}
        </Head>
      )}
      {stages.map((stage, i) => {
        const percent = total ? (stage.count / total) * 100 : 0;
        return (
          <Row key={i}>
            <Label>
              <Dot $color={stage.color} />
              {stage.label}
            </Label>
            <Bar $color={stage.color} $percent={percent}>
              <BarCount>{stage.count}</BarCount>
            </Bar>
            <Percent>{total ? Math.round(percent) : 0}%</Percent>
          </Row>
        );
      })}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Head = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 4px;
`;

const Title = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: var(--ink);
`;

const Meta = styled.div`
  font-size: 11.5px;
  color: var(--ink-faint);
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Label = styled.div`
  font-size: 12.5px;
  color: var(--ink-muted);
  width: 80px;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const Dot = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const Bar = styled.div<{ $color: string; $percent: number }>`
  flex: 1;
  height: 28px;
  border-radius: 4px;
  position: relative;
  background: var(--paper);
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: ${({ $percent }) => Math.max($percent, 4)}%;
    background: ${({ $color }) => $color};
    border-radius: 4px;
    transition: width 0.3s ease;
  }
`;

const BarCount = styled.span`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  height: 100%;
  padding-left: 10px;
  font-size: 12px;
  font-weight: 600;
  color: var(--white);
`;

const Percent = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: var(--ink-muted);
  min-width: 32px;
  text-align: right;
  flex-shrink: 0;
`;
